import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import { SectionHeader } from '../components/SectionHeader';
import { DetailFieldRow, DetailValue } from '../components/DetailFields';

function parseBool(value) {
  if (value == null || value === '') return null;
  if (value === true || value === 'true' || value === '1' || value === 1) return true;
  if (value === false || value === 'false' || value === '0' || value === 0) return false;
  return null;
}

function formatOnboardingStatus(value) {
  if (value === true) return 'Complete';
  if (value === false) return 'Pending';
  return '—';
}

function UpdateOnboardingModal({ open, field, currentValue, onClose, onSuccess }) {
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    if (open) {
      setSelected(currentValue);
      setError(null);
    }
  }, [open, currentValue]);

  const unchanged = selected === currentValue;

  // TODO: wire up your API call here.
  const handleSave = () => {
    if (!field || unchanged) return;
    onSuccess?.();
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: '12px',
          border: '1px solid #e4e7ec',
          boxShadow: '0 4px 20px rgba(24, 20, 54, 0.08)',
          bgcolor: '#fff',
          maxWidth: 420,
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2.5,
          pb: 2,
          borderBottom: '1px solid #e4e7ec',
          position: 'relative',
        }}
      >
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            color: '#9ca3af',
            '&:hover': { bgcolor: '#f3f4f6', color: '#6b7280' },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: 20 }} />
        </IconButton>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 4 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              bgcolor: '#f3f4f6',
              border: '1px solid #e4e7ec',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <TaskAltOutlinedIcon sx={{ fontSize: 20, color: '#6b7280' }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontFamily: 'Inter Bold, sans-serif',
                fontSize: 16,
                fontWeight: 700,
                color: '#181436',
                lineHeight: 1.2,
              }}
            >
              Update onboarding
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Inter Regular, sans-serif',
                fontSize: 12,
                color: '#6b7280',
                mt: 0.25,
              }}
            >
              {field?.label ?? ''}
            </Typography>
          </Box>
        </Box>
      </Box>

      <DialogContent sx={{ px: 2.5, py: 2.5, bgcolor: '#fafafa' }}>
        <Box
          sx={{
            p: 1.5,
            mb: 2,
            borderRadius: '10px',
            bgcolor: '#f3f4f6',
            border: '1px solid #e4e7ec',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Inter Regular, sans-serif',
              fontSize: 11,
              color: '#9ca3af',
              mb: 0.5,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontWeight: 600,
            }}
          >
            Current status
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Inter Bold, sans-serif',
              fontSize: 14,
              fontWeight: 600,
              color: '#6b7280',
            }}
          >
            {formatOnboardingStatus(currentValue)}
          </Typography>
        </Box>

        {error ? (
          <Typography
            sx={{
              mb: 2,
              fontFamily: 'Inter Regular, sans-serif',
              fontSize: 13,
              color: '#dc2626',
            }}
          >
            {error}
          </Typography>
        ) : null}

        <Typography
          sx={{
            fontFamily: 'Inter Regular, sans-serif',
            fontSize: 11,
            color: '#9ca3af',
            mb: 1,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            fontWeight: 600,
          }}
        >
          Set to
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {[true, false].map((val) => {
            const isSelected = selected === val;
            return (
              <Box
                key={String(val)}
                onClick={() => setSelected(val)}
                sx={{
                  flex: 1,
                  p: 1.5,
                  textAlign: 'center',
                  border: isSelected ? '1.5px solid #6b7280' : '1.5px solid #e4e7ec',
                  borderRadius: '10px',
                  bgcolor: isSelected ? '#f3f4f6' : '#fff',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  '&:hover': { bgcolor: '#f3f4f6', borderColor: '#d1d5db' },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Inter Bold, sans-serif',
                    fontSize: 13,
                    fontWeight: 600,
                    color: isSelected ? '#181436' : '#6b7280',
                  }}
                >
                  {val ? 'Complete' : 'Pending'}
                </Typography>
              </Box>
            );
          })}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mt: 2.5 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={onClose}
            sx={{
              height: 40,
              borderRadius: '8px',
              textTransform: 'none',
              fontFamily: 'Inter Regular, sans-serif',
              fontSize: 14,
              color: '#6b7280',
              borderColor: '#e4e7ec',
              '&:hover': { borderColor: '#d1d5db', bgcolor: '#f3f4f6' },
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleSave}
            disabled={unchanged || selected === null}
            sx={{
              height: 40,
              borderRadius: '8px',
              textTransform: 'none',
              fontFamily: 'Inter Bold, sans-serif',
              fontSize: 14,
              fontWeight: 600,
              bgcolor: '#181436',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#2d2a4a', boxShadow: 'none' },
              '&.Mui-disabled': { bgcolor: '#e5e7eb', color: '#9ca3af' },
            }}
          >
            Save changes
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

const ONBOARDING_FIELDS = [
  {
    apiKey: 'ein_menu_uploaded',
    label: 'EIN menu uploaded',
    description: 'Employer Identification Number and menu documents have been uploaded to the system.',
    locked: true,
    storeKey: (op) =>
      parseBool(op?.onborading_status?.ein_menu_uploaded ?? op?.onboarding_status?.ein_menu_uploaded),
  },
  {
    apiKey: 'menu_verified',
    label: 'Menu verified',
    description: 'Store menu has been reviewed and verified by the managing branch team.',
    locked: false,
    storeKey: (op) =>
      parseBool(op?.onborading_status?.menu_verified ?? op?.onboarding_status?.menu_verified),
  },
  {
    apiKey: 'device_assigned',
    label: 'Device assigned',
    description: 'A POS or order management device has been assigned and configured for this store.',
    locked: false,
    storeKey: (op) =>
      parseBool(op?.onborading_status?.device_assigned ?? op?.onboarding_status?.device_assigned),
  },
  {
    apiKey: 'dedicated_manager_assigned',
    label: 'Dedicated manager assigned',
    description: 'A dedicated account manager has been assigned to support and manage this store.',
    locked: false,
    storeKey: (op) =>
      parseBool(
        op?.onborading_status?.dedicated_manager_assigned ??
          op?.onboarding_status?.dedicated_manager_assigned
      ),
  },
];

export function OnboardingSection({ meta, store, onStoreRefresh }) {
  const op = store?.kitchen_operation ?? {};
  const [editingField, setEditingField] = useState(null);

  return (
    <>
      <SectionHeader Icon={meta.Icon} title={meta.title} subtitle={meta.subtitle} neutral />

      {ONBOARDING_FIELDS.map((field) => {
        const value = field.storeKey(op);
        return (
          <DetailFieldRow key={field.apiKey} label={field.label} description={field.description}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
              <DetailValue>{formatOnboardingStatus(value)}</DetailValue>
              {field.locked ? (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.4,
                    color: '#9ca3af',
                    fontSize: 11,
                    fontFamily: 'Inter Regular, sans-serif',
                  }}
                >
                  <LockOutlinedIcon sx={{ fontSize: 12 }} />
                  Read-only
                </Box>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setEditingField(field)}
                  sx={{
                    height: 28,
                    minWidth: 0,
                    px: 1.25,
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontFamily: 'Inter Regular, sans-serif',
                    fontSize: 12,
                    color: '#6b7280',
                    borderColor: '#e4e7ec',
                    '&:hover': { borderColor: '#d1d5db', bgcolor: '#f3f4f6' },
                  }}
                >
                  Update
                </Button>
              )}
            </Box>
          </DetailFieldRow>
        );
      })}

      <UpdateOnboardingModal
        open={Boolean(editingField)}
        field={editingField}
        currentValue={editingField ? editingField.storeKey(op) : null}
        onClose={() => setEditingField(null)}
        onSuccess={() => {
          setEditingField(null);
          onStoreRefresh?.();
        }}
      />
    </>
  );
}
